/*
THESIS: Explain source usage alongside the account's recorded plan.
OWN-WORLD: Inherit the neutral workspace, shadcn controls and separators.
STORY: Read the period, compare measured minutes with the plan, understand gaps.
FIRST VIEWPORT: Usage title, current plan and source minutes in one compact section.
FORM: A settings readout extending the existing application shell.
*/
import { useEffect, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { authClient } from "@/lib/auth-client"
import { videoClient } from "@/lib/video-client"
import {
  formatStorageBytes,
  useStorageUsage,
} from "@/components/app/project-storage-notice"

export const Route = createFileRoute("/app/usages")({
  head: () => ({
    meta: [
      { title: "Usage | intoshort" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: UsagePage,
})

type Usage = Awaited<ReturnType<typeof videoClient.usage.summary>>
const planNames = {
  free: "Free",
  basic: "Basic",
  pro: "Pro",
  high_volume: "High volume",
}
const minutes = (seconds: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(
    seconds / 60
  )

function UsagePage() {
  const { data: session } = authClient.useSession()
  return session ? <UsageReadout key={session.user.id} /> : null
}

function UsageReadout() {
  const { storageUsage, storageError, refreshStorage } = useStorageUsage()
  const [usage, setUsage] = useState<Usage | null>(null)
  const [error, setError] = useState(false)
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    let active = true
    async function load() {
      try {
        const result = await videoClient.usage.summary()
        if (active) {
          setUsage(result)
          setError(false)
        }
      } catch {
        if (active) {
          setUsage(null)
          setError(true)
        }
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 30_000)
    window.addEventListener("focus", load)
    return () => {
      active = false
      window.clearInterval(timer)
      window.removeEventListener("focus", load)
    }
  }, [refresh])

  const month = usage
    ? new Intl.DateTimeFormat("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(usage.periodStart))
    : null
  return (
    <main className="app-main" aria-labelledby="usage-title">
      <section className="flex w-full max-w-2xl flex-col gap-8">
        <Link
          to="/app"
          className={buttonVariants({
            variant: "ghost",
            size: "sm",
            className: "app-video-back self-start",
          })}
        >
          <ArrowLeft data-icon="inline-start" aria-hidden="true" />
          Back to videos
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 id="usage-title" className="text-3xl font-semibold">
            Usage
          </h1>
          <Link
            to="/app/billing"
            className={buttonVariants({ variant: "default" })}
          >
            Manage plan
          </Link>
        </div>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>
              We could not load your usage or plan. Check your connection and
              try again.
              <Button
                variant="outline"
                onClick={() => {
                  setError(false)
                  setRefresh((value) => value + 1)
                }}
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : !usage ? (
          <div
            className="flex flex-col gap-4"
            role="status"
            aria-label="Loading usage"
          >
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-medium">
                  {usage.plan === "free" ? "Free trial" : month}
                </h2>
                <Badge variant="secondary">{planNames[usage.plan]} plan</Badge>
              </div>
              <dl className="flex flex-col gap-4">
                {usage.plan === "free" && (
                  <div className="flex flex-wrap justify-between gap-2">
                    <dt>Trial videos used</dt>
                    <dd className="font-semibold tabular-nums">
                      {usage.trialVideosUsed ?? 0} of{" "}
                      {usage.trialVideoLimit ?? 1}
                    </dd>
                  </div>
                )}
                <div className="flex flex-wrap justify-between gap-2">
                  <dt>Source minutes used</dt>
                  <dd className="font-semibold tabular-nums">
                    {minutes(usage.sourceSeconds)} min
                  </dd>
                </div>
                {usage.plan !== "free" && (
                  <div className="flex flex-wrap justify-between gap-2">
                    <dt>Monthly plan volume</dt>
                    <dd>{usage.monthlyMinutes?.toLocaleString("en-US")} min</dd>
                  </div>
                )}
                {usage.monthlyMinutes !== null && (
                  <div className="flex flex-wrap justify-between gap-2">
                    <dt>Minutes remaining</dt>
                    <dd className="tabular-nums">
                      {minutes(
                        Math.max(
                          0,
                          usage.monthlyMinutes * 60 - usage.sourceSeconds
                        )
                      )}{" "}
                      min
                    </dd>
                  </div>
                )}
              </dl>
              {usage.plan === "free" ? (
                <Progress
                  value={
                    ((usage.trialVideosUsed ?? 0) /
                      (usage.trialVideoLimit ?? 1)) *
                    100
                  }
                  aria-label="Free trial videos used"
                  aria-valuetext={`${usage.trialVideosUsed ?? 0} of ${usage.trialVideoLimit ?? 1} trial videos used`}
                />
              ) : usage.monthlyMinutes !== null ? (
                <Progress
                  value={Math.min(
                    100,
                    (usage.sourceSeconds / (usage.monthlyMinutes * 60)) * 100
                  )}
                  aria-label="Source minutes used compared with monthly plan volume"
                  aria-valuetext={`${minutes(usage.sourceSeconds)} of ${usage.monthlyMinutes} source minutes`}
                />
              ) : null}
              {usage.monthlyMinutes !== null &&
                usage.sourceSeconds > usage.monthlyMinutes * 60 && (
                  <p className="text-sm text-muted-foreground">
                    Your usage exceeds your current plan volume. New videos are
                    blocked until your quota renews or you upgrade.
                  </p>
                )}
              {usage.plan !== "free" && (
                <p className="text-sm text-muted-foreground">
                  {`${usage.measuredVideos} source ${usage.measuredVideos === 1 ? "video" : "videos"} with a reserved duration. A 60-minute source counts as 60 minutes, regardless of how many shorts it produces.`}
                </p>
              )}
              {usage.unmeasuredVideos > 0 && (
                <Alert>
                  <AlertDescription>
                    {usage.unmeasuredVideos}{" "}
                    {usage.unmeasuredVideos === 1 ? "video has" : "videos have"}{" "}
                    no measured duration yet and{" "}
                    {usage.unmeasuredVideos === 1 ? "is" : "are"} excluded from
                    this total.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </>
        )}
        <section
          className="flex flex-col gap-5"
          aria-labelledby="cloud-storage-title"
        >
          <h2 id="cloud-storage-title" className="text-lg font-medium">
            Cloud storage
          </h2>
          {storageError ? (
            <Alert variant="destructive">
              <AlertDescription className="flex flex-col items-start gap-3">
                Could not load cloud storage usage. Your space is unknown until
                this check succeeds.
                <Button variant="outline" onClick={() => void refreshStorage()}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : !storageUsage ? (
            <div
              className="flex flex-col gap-3"
              role="status"
              aria-label="Loading cloud storage usage"
            >
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <>
              <dl className="flex flex-col gap-4">
                <div className="flex flex-wrap justify-between gap-2">
                  <dt>Permanent storage allowance</dt>
                  <dd className="tabular-nums">
                    {formatStorageBytes(storageUsage.limitBytes)}
                  </dd>
                </div>
                {storageUsage.measured && (
                  <>
                    <div className="flex flex-wrap justify-between gap-2">
                      <dt>Permanent storage used and reserved</dt>
                      <dd className="font-semibold tabular-nums">
                        {formatStorageBytes(storageUsage.permanentBytes)}
                      </dd>
                    </div>
                    <div className="flex flex-wrap justify-between gap-2">
                      <dt>Of which reserved for uploads and processing</dt>
                      <dd className="tabular-nums">
                        {formatStorageBytes(storageUsage.reservedBytes)}
                      </dd>
                    </div>
                    <div className="flex flex-wrap justify-between gap-2">
                      <dt>Temporary storage used</dt>
                      <dd className="tabular-nums">
                        {formatStorageBytes(storageUsage.temporaryBytes)}
                      </dd>
                    </div>
                  </>
                )}
                <div className="flex flex-wrap justify-between gap-2">
                  <dt>Temporary projects</dt>
                  <dd className="tabular-nums">
                    {storageUsage.temporaryProjectCount}
                  </dd>
                </div>
              </dl>
              {!storageUsage.measured ? (
                <Alert variant="info" role="status">
                  <AlertDescription>
                    We are measuring your existing files. Storage usage is not
                    available yet. New projects may be temporary until this
                    check finishes.
                  </AlertDescription>
                </Alert>
              ) : storageUsage.limitBytes > 0 ? (
                <Progress
                  value={Math.min(
                    100,
                    (storageUsage.permanentBytes / storageUsage.limitBytes) *
                      100
                  )}
                  aria-label="Permanent cloud storage used and reserved"
                  aria-valuetext={`${formatStorageBytes(storageUsage.permanentBytes)} of ${formatStorageBytes(storageUsage.limitBytes)}`}
                />
              ) : null}
              <p className="text-sm text-muted-foreground">
                Storage is the space occupied by your files and does not reset
                each month. 1 GB is 1 billion bytes. Sources, shorts and
                uploaded media count toward storage.
              </p>
              <Alert variant="info" role="note">
                <AlertDescription className="flex flex-col gap-2">
                  <p>
                    {storageUsage.plan === "free"
                      ? "Free trial projects, including source videos, shorts and saved edits, are permanently deleted 3 days after creation."
                      : "When a project does not fit your permanent storage allowance, you can still generate it within your source-minute allowance. It becomes temporary and its source, shorts and saved edits are permanently deleted 3 days after creation."}
                  </p>
                  <p>
                    Download the shorts you want to keep before expiration. Free
                    enough space
                    {storageUsage.plan !== "high_volume"
                      ? " or upgrade to a plan with enough cloud storage"
                      : ""}{" "}
                    before expiration to keep temporary projects automatically.
                  </p>
                  <Link to="/app">Manage videos</Link>
                </AlertDescription>
              </Alert>
            </>
          )}
        </section>
      </section>
    </main>
  )
}
